import React from "react";
import { Check, ClipboardCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatCoachPatchOpsSummary } from "@/utils/strategyRdCoachContext";
import type { StrategyRdCoachProposal } from "@/types/strategyRdCoach";
import { strategyRdBrandActionButtonClassName } from "../strategyRdCardShell";
import { statsCardHeadlineClassName } from "../../Statistics/components/statisticsCardHeadline";

/** Card action buttons in the chat: the app's 32px Lab action button, primary font, sentence case. */
const ACTION_BUTTON_CLASS = cn(
  strategyRdBrandActionButtonClassName,
  "h-8 !px-3 text-xs normal-case tracking-normal [&>span]:!normal-case [&>span]:!tracking-normal"
);

type Props = {
  /** Composer, template and sizing changes — applied together, or none. */
  proposal: StrategyRdCoachProposal;
  confirming: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
};

export function StrategyRdCoachConfirmApply({
  proposal,
  confirming,
  onConfirm,
  onDismiss,
}: Props) {
  const lines = [
    ...formatCoachPatchOpsSummary(proposal.composer_patch),
    ...(proposal.patch_readback ?? []),
  ];
  const hasRules = Boolean(
    proposal.template ||
      proposal.template_patch ||
      proposal.sizing ||
      proposal.sizing_patch
  );
  return (
    <div
      className="flex flex-col overflow-hidden rounded-[8px] !border !border-solid !border-[color:var(--brand-tropical-surf)]/35 bg-card font-[family-name:var(--font-primary)] shadow-[0_4px_16px_rgba(0,0,0,0.04)]"
      data-strategy-rd-confirm-apply=""
    >
      <div className="flex flex-col gap-1.5 !px-4 !pb-3 !pt-3.5">
        <p className="!m-0 flex items-center gap-1.5 !text-[15px] !font-medium leading-6 text-[color:var(--brand-tropical-surf)]">
          <ClipboardCheck className="size-4" aria-hidden />
          Confirm update
        </p>
        <p className="!m-0 text-xs leading-5 text-gray-500 dark:text-muted-foreground">
          TES suggested Lab changes. Nothing changes until you confirm.
          {hasRules ? " The rule editors then open filled in, so you can check them before a backtest." : ""}
        </p>
      </div>
      {lines.length > 0 ? (
        <ul
          data-strategy-rd-patch-readback=""
          className="!mx-4 !mb-4 !mt-0 flex max-h-40 !list-none flex-col gap-1.5 overflow-y-auto rounded-[6px] !border !border-solid !border-border bg-muted/40 !px-3 !py-2.5 text-xs leading-5"
        >
          {lines.map((line) => {
            const at = line.indexOf(": ");
            return (
              <li key={line} className="text-gray-600 dark:text-foreground">
                {at > 0 ? (
                  <>
                    <span className={cn(statsCardHeadlineClassName, "!text-[11px] !leading-5")}>{line.slice(0, at)}</span>{" "}
                    {line.slice(at + 2)}
                  </>
                ) : (
                  line
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2 !border-0 !border-t !border-solid !border-border bg-muted/30 !px-4 !py-3">
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
          className={ACTION_BUTTON_CLASS}
          disabled={confirming}
          onClick={onDismiss}
        >
          <span className="normal-case tracking-normal">Dismiss</span>
        </Button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className={cn(ACTION_BUTTON_CLASS, "gap-1.5 text-white [&_svg]:text-white")}
          disabled={confirming}
          onClick={onConfirm}
        >
          <Check className="size-3.5" aria-hidden />
          <span className="normal-case tracking-normal">{confirming ? "Updating…" : "Confirm update"}</span>
        </Button>
      </div>
    </div>
  );
}
